import React from "react";
import { Grid } from "@material-ui/core";
import { useTranslate } from "react-admin";
import {
  useIdlingOffDutyAverage,
  useIdlingOnDutyAverage,
  useTotalIdlingFuelConsumption,
  useMaxIdlingTime,
  useMinIdlingTime,
  useAverageIdlingTime,
  IdleReportsHookParams,
} from "../hooks/useIdleReportsHooks";
import ReportCardWidget from "../fuel-economy-report/ReportCardWidget";
import { round } from "../fuel-economy-report/chartUtils";

/** Backend returns time values in seconds; convert to minutes for display. */
const secondsToMinutes = (seconds: number): number => seconds / 60;
const stopsSecondsToMinutes = (
  stops: number[] | null | undefined,
): number[] | undefined => stops?.map(secondsToMinutes);

export interface IdleReportBaseMetricsProps extends IdleReportsHookParams {}

export const IdleReportBaseMetrics: React.FC<IdleReportBaseMetricsProps> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const params = { fromDate, toDate, periodType };

  const offDuty = useIdlingOffDutyAverage(params);
  const onDuty = useIdlingOnDutyAverage(params);
  const fuelCons = useTotalIdlingFuelConsumption(params);
  const maxIdle = useMaxIdlingTime(params);
  const minIdle = useMinIdlingTime(params);
  const avgIdle = useAverageIdlingTime(params);

  const metrics: Array<{
    labelKey: string;
    unit: string;
    value: string | null;
    stops?: number[];
    isGrowing?: boolean;
    growth?: number;
    isReverse?: boolean;
    isLoading?: boolean;
    isError?: boolean;
  }> = [
    {
      labelKey: "idleReport.idleOffDutyAverage",
      unit: "min",
      value:
        offDuty.data?.value != null
          ? round(secondsToMinutes(offDuty.data.value))
          : null,
      stops: stopsSecondsToMinutes(offDuty.data?.stops),
      isGrowing: offDuty.data?.isGrowing,
      growth: offDuty.data?.growth,
      isLoading: offDuty.loading,
      isError: !!offDuty.error,
    },
    {
      labelKey: "idleReport.idleOnDutyAverage",
      unit: "min",
      value:
        onDuty.data?.value != null
          ? round(secondsToMinutes(onDuty.data.value))
          : null,
      stops: stopsSecondsToMinutes(onDuty.data?.stops),
      isGrowing: onDuty.data?.isGrowing,
      growth: onDuty.data?.growth,
      isLoading: onDuty.loading,
      isError: !!onDuty.error,
    },
    {
      labelKey: "idleReport.idleModeFuelConsumption",
      unit: "gal",
      value: fuelCons.data?.value != null ? round(fuelCons.data.value) : null,
      stops: fuelCons.data?.stops ?? undefined,
      isGrowing: fuelCons.data?.isGrowing,
      growth: fuelCons.data?.growth,
      isReverse: true,
      isLoading: fuelCons.loading,
      isError: !!fuelCons.error,
    },
    {
      labelKey: "idleReport.maxIdleTime",
      unit: "min",
      value:
        maxIdle.data?.value != null
          ? round(secondsToMinutes(maxIdle.data.value))
          : null,
      stops: stopsSecondsToMinutes(maxIdle.data?.stops),
      isGrowing: maxIdle.data?.isGrowing,
      growth: maxIdle.data?.growth,
      isLoading: maxIdle.loading,
      isError: !!maxIdle.error,
    },
    {
      labelKey: "idleReport.minIdleTime",
      unit: "min",
      value:
        minIdle.data?.value != null
          ? round(secondsToMinutes(minIdle.data.value))
          : null,
      stops: stopsSecondsToMinutes(minIdle.data?.stops),
      isGrowing: minIdle.data?.isGrowing,
      growth: minIdle.data?.growth,
      isLoading: minIdle.loading,
      isError: !!minIdle.error,
    },
    {
      labelKey: "idleReport.averageIdleTime",
      unit: "min",
      value:
        avgIdle.data?.value != null
          ? round(secondsToMinutes(avgIdle.data.value))
          : null,
      stops: stopsSecondsToMinutes(avgIdle.data?.stops),
      isGrowing: avgIdle.data?.isGrowing,
      growth: avgIdle.data?.growth,
      isLoading: avgIdle.loading,
      isError: !!avgIdle.error,
    },
  ];

  return (
    <Grid container spacing={2} style={{ height: "100%", width: "100%" }}>
      {metrics.map((m) => (
        <Grid item xs={12} sm={6} key={m.labelKey} style={{ display: "flex" }}>
          <ReportCardWidget
            title={translate(m.labelKey, { _: m.labelKey })}
            value={m.value ?? "-"}
            unit={m.unit}
            stops={m.stops ?? undefined}
            isGrowing={m.isGrowing}
            growth={m.growth}
            isReverse={m.isReverse ?? false}
            isLoading={m.isLoading}
            isError={m.isError}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default IdleReportBaseMetrics;
